import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

export const Reveal = ({ children, delay = 0, className, y = 28 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.85, delay, ease: [0.22, 1, 0.36, 1] }}
  >
    {children}
  </motion.div>
);

export const RoseMark = ({ className = "w-8 h-8 text-gold" }) => {
  const spokes = [0, 45, 90, 135, 180, 225, 270, 315];
  return (
    <svg viewBox="0 0 48 48" className={className} fill="none" aria-hidden="true">
      <circle cx="24" cy="24" r="21" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="24" cy="24" r="6" stroke="currentColor" strokeWidth="1.4" />
      {spokes.map((a) => (
        <line
          key={a}
          x1="24"
          y1="24"
          x2={24 + 21 * Math.cos((a * Math.PI) / 180)}
          y2={24 + 21 * Math.sin((a * Math.PI) / 180)}
          stroke="currentColor"
          strokeWidth="1"
          opacity="0.7"
        />
      ))}
      <circle cx="24" cy="24" r="2.4" fill="currentColor" />
    </svg>
  );
};

export const SectionHeading = ({ eyebrow, title, dark = false, align = "left", testid }) => (
  <Reveal className={align === "center" ? "text-center" : "text-left"}>
    <div className={`flex items-center gap-3 ${align === "center" ? "justify-center" : ""}`}>
      <span className="diamond" aria-hidden="true" />
      <span
        className={`font-mono text-xs sm:text-sm tracking-[0.25em] uppercase ${
          dark ? "text-goldlight" : "text-gold"
        }`}
      >
        {eyebrow}
      </span>
    </div>
    <h2
      data-testid={testid}
      className={`mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight ${
        dark ? "text-ivory" : "text-espresso"
      }`}
    >
      {title}
    </h2>
    <div
      className={`mt-5 h-px w-24 bg-gradient-to-r from-gold to-transparent ${
        align === "center" ? "mx-auto" : ""
      }`}
      aria-hidden="true"
    />
  </Reveal>
);

export const Modal = ({ open, onClose, image, title, children, testid }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        className="fixed inset-0 z-[90] flex items-center justify-center p-4 sm:p-6"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid={testid}
      >
        <motion.button
          aria-label="Chiudi"
          className="absolute inset-0 bg-ink/75 cursor-pointer"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid={`${testid}-backdrop`}
        />
        <motion.div
          role="dialog"
          aria-modal="true"
          className="relative bg-ivory w-full max-w-2xl max-h-[85vh] overflow-y-auto border border-stone shadow-[0_30px_80px_rgba(26,22,19,0.45)]"
          initial={{ opacity: 0, y: 40, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.98 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <button
            onClick={onClose}
            aria-label="Chiudi finestra"
            data-testid={`${testid}-close`}
            className="absolute top-3 right-3 z-10 flex items-center justify-center w-10 h-10 bg-ink/70 text-ivory hover:bg-gold hover:text-ink transition-colors duration-300 rounded-full"
          >
            <X size={18} />
          </button>
          {image && (
            <div className="w-full h-56 sm:h-72 overflow-hidden">
              <img src={image} alt={title} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="p-6 sm:p-9">
            <h3 className="font-serif text-2xl sm:text-3xl font-bold text-espresso tracking-tight">
              {title}
            </h3>
            <div className="mt-3 h-px w-16 bg-gold" aria-hidden="true" />
            <div className="mt-5 text-base sm:text-lg text-taupe leading-relaxed space-y-4">
              {children}
            </div>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);
